/**
 * Cart context — local-only (localStorage). No backend persistence.
 *
 * Each cart line = { variant_id, product_id, product_name, image, variant_title,
 *                    unit_price_eur, currency, quantity }.
 *
 * Re-pricing happens server-side at checkout, so the price stored here is
 * informational only (display in cart, totals preview).
 */
import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

const KEY = "geovinum_cart_v1";

const CartCtx = createContext(null);

function readStorage() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => readStorage());

  useEffect(() => {
    try { localStorage.setItem(KEY, JSON.stringify(items)); } catch { /* quota / private */ }
  }, [items]);

  const addItem = useCallback((line) => {
    if (!line?.variant_id) return;
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.variant_id === line.variant_id);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], quantity: Math.min(10, copy[idx].quantity + (line.quantity || 1)) };
        return copy;
      }
      return [...prev, { ...line, quantity: Math.min(10, line.quantity || 1) }];
    });
  }, []);

  const removeItem = useCallback((variant_id) => {
    setItems((prev) => prev.filter((i) => i.variant_id !== variant_id));
  }, []);

  const setQty = useCallback((variant_id, qty) => {
    const q = Math.max(1, Math.min(10, Number(qty) || 1));
    setItems((prev) => prev.map((i) => i.variant_id === variant_id ? { ...i, quantity: q } : i));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const totals = useMemo(() => {
    const subtotal = items.reduce((s, i) => s + (Number(i.unit_price_eur) || 0) * i.quantity, 0);
    const count = items.reduce((s, i) => s + i.quantity, 0);
    return { subtotal: Math.round(subtotal * 100) / 100, count };
  }, [items]);

  const value = useMemo(() => ({ items, addItem, removeItem, setQty, clear, totals }), [items, addItem, removeItem, setQty, clear, totals]);
  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}

export function useCart() {
  const v = useContext(CartCtx);
  if (!v) throw new Error("useCart must be used within <CartProvider>");
  return v;
}
